import { useAppContext } from '../../../../context'
import { Alert, Box, Container, ExpandableSection } from '@amzn/awsui-components-react'
import { useEffect, useState } from 'react'
import TeamAllocationSummaryHeaderPanel from './TeamAllocationSummaryHeaderPanel'
import {
    generateTeamAllocationSummaryGridOptions,
    getFormatFixedColumns,
    nullOutZeroAndDivideValuesByNumberOfWeek,
} from './Util'
import useStore from '../../../Store'
import {
    getDateStringYearMonth,
    getLastFriday,
    isDateEnabled,
} from '../../reusable/AllocationUtils'
import BusinessEntityRefresh from '../../reusable/BusinessEntityRefresh'
import { AlertTypes, TIME_STRING, YEAR_MONTH_REGEX } from '../../../Constant'
import './ManagerPage.scss'
import { AgGridReact } from 'ag-grid-react'
import ManagerTeamHeaderGroup from './ManagerTeamHeaderGroup'

const ManagerPage = () => {
    const appContext = useAppContext()
    const apiClient = appContext.apiClient
    const userAlias = appContext.userAlias
    const lastFridayYearMonth = getDateStringYearMonth(getLastFriday(new Date()))

    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)

    const [gridApi, setGridApi] = useState<any>(null)
    const [isWeekClosed, setIsWeekClosed] = useState<boolean>(false)
    const [myTeams, setMyTeams] = useState<any[]>([])
    const [date, setDate] = useState<string>(lastFridayYearMonth)
    const [dateErrorText, setDateErrorText] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [alertContent, setAlertContent] = useState<string>('')
    const [isWeekCloseLoading, setWeekCloseIsLoading] = useState<boolean>(false)
    const [teamColumns, setTeamColumns] = useState<any>([])
    const [teamRows, setTeamRows] = useState<any>(null)
    const [isMonthlyView, setIsMonthlyView] = useState<boolean>(true)
    const [closedMonths, setClosedMonths] = useState<string[]>([])

    const onGridReady = (params) => {
        setGridApi(params.api)
        params.api.setDomLayout('autoHeight')
    }

    const onEstimateModelUpdated = (params) => {
        if (params.api.getDisplayedRowCount() === 0) {
            params.api.showNoRowsOverlay()
        }
    }

    const handleExport = () => {
        const now = new Date()
        gridApi?.exportDataAsExcel({
            fileName: `Falcon Team Allocation Summary ${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}.xlsx`,
            shouldRowBeSkipped: (params) => params.node.footer,
            processRowGroupCallback: (params) => params.node.key,
        })
    }

    const getSummaryColumnsRows = () => {
        const orgId = myTeams[0]['org_id']
        const team_ids = myTeams.map((team) => team['team_id']).join(',')
        const url = `/team-allocation-summary/business-entity/${selectBusinessEntity.id}/allocation-${isMonthlyView ? 'month' : 'week'}/${date}?team_ids=${team_ids}`

        setIsLoading(true)
        gridApi.showLoadingOverlay()
        getAllOrgsByBusinessEntityDate(orgId)

        apiClient
            .get(url)
            .then((res) => {
                const teamMap = new Map(
                    Object.entries(Object.fromEntries(myTeams.map((t) => [t.team_id, t]))),
                )
                const teamInfo = res.data.cols.map((team) => ({
                    ...team,
                    ...(teamMap.get(team.team_id) ?? {}),
                }))
                setTeamColumns(
                    getFormatFixedColumns(teamInfo, ManagerTeamHeaderGroup, {
                        isMonthlyView,
                        date,
                    }),
                )
                setTeamRows(
                    nullOutZeroAndDivideValuesByNumberOfWeek(
                        res.data.rows,
                        Object.keys(teamInfo[0].scoped_weeks).length,
                    ),
                )
            })
            .catch((error) => {
                console.error(error)
                setTeamRows([])
            })
            .finally(() => setIsLoading(false))
    }

    const getMyTeamsClosedMonths = () => {
        apiClient
            .get(`/user/${userAlias}/business-entity/${selectBusinessEntity.id}/myteam`)
            .then(async (response) => {
                const myTeams = response.data.filter((team) => team.is_active && !team?.archived)
                if (!myTeams.length) {
                    setAlertContent('No registered team found.')
                    return
                }
                await apiClient
                    .get(`/close-status/orgs/${myTeams[0]['org_id']}/months`)
                    .then((response) => {
                        const months = response.data
                        if (months?.length) {
                            setClosedMonths(months.sort())
                            setDate(months[months.length - 1])
                        }
                    })
                    .catch((error) => {
                        console.error(error)
                    })

                setAlertContent('')
                setMyTeams(myTeams)
            })
            .catch((error) => {
                console.error(error)
            })
    }

    const getAllOrgsByBusinessEntityDate = (orgId: string) => {
        setWeekCloseIsLoading(true)
        apiClient
            .get(`/admin/dashboard/business-entity/${selectBusinessEntity.id}/week/${date}`)
            .then((response) => {
                const allOrgs = response.data
                const org = allOrgs.find((org) => org.org_id === orgId)
                if (!org) {
                    setAlertContent('No organization found for registered team.')
                    return
                }

                setIsWeekClosed(org.week_closed)
            })
            .catch((error) => {
                console.error(error)
            })
            .finally(() => setWeekCloseIsLoading(false))
    }

    useEffect(() => {
        BusinessEntityRefresh(selectBusinessEntity.id, getMyTeamsClosedMonths)
    }, [selectBusinessEntity])

    useEffect(() => {
        const dateObject = new Date(date + TIME_STRING)

        if (!gridApi) {
            return
        }
        if (!myTeams.length) {
            setTeamRows([])
            setIsLoading(false)
            return
        }
        if (isMonthlyView) {
            if (!YEAR_MONTH_REGEX.test(date)) {
                setDateErrorText('Invalid date. Use YYYY/MM format. Closed month only.')
                return
            }
            if (!closedMonths) {
                setAlertContent('No closed month found. Contact admin for more information.')
                setIsLoading(false)
                return
            }
        } else if (!isDateEnabled(dateObject)) {
            setDateErrorText('Invalid date. Use YYYY/MM/DD format. Friday only.')
            return
        }

        setDateErrorText('')
        getSummaryColumnsRows()
    }, [myTeams, date])

    return (
        <Container>
            <ExpandableSection defaultExpanded headerText='Team Allocation Summary'>
                <Box margin={{ top: 's' }}>
                    {alertContent ? (
                        <Alert statusIconAriaLabel='Warning' type={AlertTypes.WARNING}>
                            {alertContent}
                        </Alert>
                    ) : (
                        <Container
                            fitHeight
                            header={
                                <TeamAllocationSummaryHeaderPanel
                                    isWeekCloseLoading={isWeekCloseLoading}
                                    isWeekClosed={isWeekClosed}
                                    isLoading={isLoading}
                                    dateErrorText={dateErrorText}
                                    date={date}
                                    onDateChange={setDate}
                                    onExportClick={handleExport}
                                    isMonthlyView={isMonthlyView}
                                    onIsMonthlyViewChange={setIsMonthlyView}
                                    closedMonths={closedMonths}
                                />
                            }
                        >
                            <div className='ag-theme-quartz' style={{ height: 'fit-content' }}>
                                <AgGridReact
                                    rowData={teamRows}
                                    columnDefs={teamColumns}
                                    onGridReady={onGridReady}
                                    onModelUpdated={onEstimateModelUpdated}
                                    gridOptions={generateTeamAllocationSummaryGridOptions()}
                                    groupTotalRow={'bottom'}
                                    grandTotalRow={'bottom'}
                                    autoGroupColumnDef={{
                                        headerName: teamColumns?.[0]?.headerName,
                                        pinned: 'left',
                                    }}
                                />
                            </div>
                        </Container>
                    )}
                </Box>
            </ExpandableSection>
        </Container>
    )
}

export default ManagerPage
